<template>
  <teleport to="body">
    <transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="show"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click="handleBackdropClick"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black/50 backdrop-blur-sm"></div>
        
        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 scale-95 translate-y-4"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 translate-y-4"
          >
            <div
              v-if="show"
              @click.stop
              class="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl transition-all"
            >
              <!-- Modal Header -->
              <div class="px-6 pt-6 pb-4">
                <div class="flex items-center gap-4">
                  <!-- Icon -->
                  <div class="flex-shrink-0">
                    <div class="flex h-12 w-12 items-center justify-center rounded-full" :class="iconBgClass">
                      <component :is="icon" class="h-6 w-6" :class="iconClass" />
                    </div>
                  </div>
                  
                  <!-- Title and Description -->
                  <div class="flex-1">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                      {{ title }}
                    </h3>
                    <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {{ description }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Modal Body -->
              <div class="px-6 pb-4" v-if="$slots.default">
                <slot />
              </div>

              <!-- Modal Footer -->
              <div class="px-6 pb-6">
                <div class="flex gap-3">
                  <!-- Cancel Button -->
                  <button
                    @click="$emit('cancel')"
                    :disabled="loading"
                    class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ cancelText }}
                  </button>
                  
                  <!-- Confirm Button -->
                  <button
                    @click="$emit('confirm')"
                    :disabled="loading"
                    class="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
                    :class="confirmButtonClass"
                  >
                    <div v-if="loading" class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    {{ loading ? loadingText : confirmText }}
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    required: true
  },
  description: {
    type: String,
    default: ''
  },
  icon: {
    type: [Object, Function],
    required: true
  },
  iconType: {
    type: String,
    default: 'danger', // 'danger', 'warning', 'info', 'success'
    validator: (value) => ['danger', 'warning', 'info', 'success'].includes(value)
  },
  confirmText: {
    type: String,
    default: 'Confirm'
  },
  cancelText: {
    type: String,
    default: 'Cancel'
  },
  loadingText: {
    type: String,
    default: 'Loading...'
  },
  loading: {
    type: Boolean,
    default: false
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['confirm', 'cancel'])

const iconBgClass = computed(() => {
  switch (props.iconType) {
    case 'danger':
      return 'bg-red-100 dark:bg-red-900/20'
    case 'warning':
      return 'bg-yellow-100 dark:bg-yellow-900/20'
    case 'info':
      return 'bg-blue-100 dark:bg-blue-900/20'
    case 'success':
      return 'bg-green-100 dark:bg-green-900/20'
    default:
      return 'bg-gray-100 dark:bg-gray-900/20'
  }
})

const iconClass = computed(() => {
  switch (props.iconType) {
    case 'danger':
      return 'text-red-600 dark:text-red-400'
    case 'warning':
      return 'text-yellow-600 dark:text-yellow-400'
    case 'info':
      return 'text-blue-600 dark:text-blue-400'
    case 'success':
      return 'text-green-600 dark:text-green-400'
    default:
      return 'text-gray-600 dark:text-gray-400'
  }
})

const confirmButtonClass = computed(() => {
  switch (props.iconType) {
    case 'danger':
      return 'bg-red-600 hover:bg-red-700 disabled:bg-red-400 focus:ring-red-500'
    case 'warning':
      return 'bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 focus:ring-yellow-500'
    case 'info':
      return 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 focus:ring-blue-500'
    case 'success':
      return 'bg-green-600 hover:bg-green-700 disabled:bg-green-400 focus:ring-green-500'
    default:
      return 'bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 focus:ring-gray-500'
  }
})

function handleBackdropClick() {
  if (props.closeOnBackdrop && !props.loading) {
    emit('cancel')
  }
}

function handleKeydown(event) {
  if (event.key === 'Escape' && props.show && !props.loading) {
    emit('cancel')
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>